import renderInputField from 'components/FormControls/textInput'
import { Button } from 'gu-ui-kit'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import * as Yup from 'yup'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import { ROUTES } from 'constants/routes'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import AdvertiserInfoGroup from 'modules/Forms/Account/Create/subcomponents/AdvertiserInfoGroup'
import { FETCH_NETWORK_SETTINGS } from 'modules/Services/types'

export const VkSchema = () =>
  Yup.object()
    .shape({
      logins: Yup.array().of(Yup.object()),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', baseLoginValidator)
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      vk_profile_url: Yup.string().required('common::validation_messages.required'),
      vk_token: Yup.string(),
    })
    .concat(advertiserInfoValidationRulesExtended())

let CreateVkAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  formHeader,
  form,
  change,
  networkId,
  array,
  multiLogins,
  initialValues,
}) => {
  let childWindow
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)

  const {
    formValues: { advertiser_info, vk_profile_url },
    settings = null,
  } = useSelector((state) => ({
    formValues: selector(state, 'vk_profile_url', 'advertiser_info'),
    settings: state['services/account-create'].settings,
  }))

  global.getBack = useCallback(
    ({ profile_url, access_token }) => {
      !!childWindow && childWindow.close()
      childWindow = null

      autofill('vk_profile_url', profile_url)
      autofill('vk_token', access_token)
    },
    [childWindow, autofill],
  )

  useEffect(() => {
    if (!networkId) {
      return
    }

    dispatch({
      type: FETCH_NETWORK_SETTINGS,
      payload: { networkId },
    })
  }, [networkId])

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['advertiser_template'])),
    [submitAccountData],
  )

  const openConnectWindow = useCallback(() => {
    childWindow = global.open(
      ROUTES.VK_AUTH,
      'Vk oAuth dialog',
      'resizable,toolbar=yes,location=yes,scrollbars=yes,menubar=yes,width=800,height=600,top=0,left=0',
    )
  }, [childWindow])

  useEffect(() => {
    return () => {
      global.getBack = undefined
    }
  }, [])

  return (
    <>
      {formHeader}

      <form
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />
        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.vk.fields.login.label'}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="vk_profile_url"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.vk.fields.vk_profile_url.label')}
            placeholder={t('services::forms.register_account.vk.fields.vk_profile_url.label')}
            readOnly={!settings || !!settings?.user_can_create_multiple_accounts === false}
            t={t}
            InputProps={{
              endAdornment: !settings
                ? null
                : !vk_profile_url &&
                  !!settings?.user_can_create_multiple_accounts === false && (
                    <Button
                      variant={'link'}
                      onClick={openConnectWindow}
                      color={'Label-GU-Primary'}
                      noWrap
                    >
                      {t('services::forms.register_account.vk.fields.vk_profile_url.get_url_label')}
                    </Button>
                  ),
            }}
          />
        </div>

        <Field
          hidden
          name="vk_token"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.vk.fields.vk_token.label')}
          t={t}
        />

        <AdvertiserInfoGroup
          form={form}
          change={change}
          advertiser_info={advertiser_info}
          networkType={networkType}
          initialValues={initialValues}
        />

        <SubmitButtons
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateVkAccountForm = reduxForm({
  form: 'CreateVkAccountForm',
  initialValues: {
    login: '',
    network: '',
    vk_profile_url: '',
    vk_token: '',
    payment_profile_id: undefined,
    advertiser_info: initialAdvertiserInfo,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateVkAccountForm)

CreateVkAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  valid: PropTypes.bool,
  pristine: PropTypes.bool,
  autofill: PropTypes.func,
  change: PropTypes.func,
  networkType: PropTypes.string,
  networkId: PropTypes.string,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  initialValues: PropTypes.bool,
  formHeader: PropTypes.node,
}

export default CreateVkAccountForm
